import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { servicoService } from '@/services/servico.service'
import { associadoService } from '@/services/associado.service'
import { supabase } from '@/config/supabase'
import { formatCurrency } from '@/lib/formatters'
import { usePortalStore } from '@/stores/portal.store'
import type { Servico, Associado, CheckoutBranding, ServicoPlano, ServicoAdicional } from '@/types/database'
import {
    Loader2, Shield, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, ScrollText,
    User, Phone, Mail, Calendar, MessageCircle, KeyRound, ExternalLink, Heart, Sparkles,
    Layers, Plus, Minus, ListPlus, ShoppingBag, Star
} from 'lucide-react'

/* ================================================================
   Helper — pro-rata
   ================================================================ */
function calcProRata(v: number) {
    const today = new Date()
    const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
    const rem = dim - today.getDate() + 1
    return Math.round((v / dim) * rem * 100) / 100
}

/* ================================================================
   CPF mask
   ================================================================ */
function maskCpf(value: string) {
    const d = value.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 3) return d
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

function maskPhone(value: string) {
    const d = value.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 2) return `(${d}`
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

function maskCnpj(value: string) {
    const d = value.replace(/\D/g, '').slice(0, 14)
    if (d.length <= 2) return d
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

function maskCep(value: string) {
    const d = value.replace(/\D/g, '').slice(0, 8)
    if (d.length <= 5) return d
    return `${d.slice(0, 5)}-${d.slice(5)}`
}

function maskPlaca(value: string) {
    const v = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
    if (v.length <= 3) return v
    return `${v.slice(0, 3)}-${v.slice(3, 7)}`
}

function maskDate(value: string) {
    const d = value.replace(/\D/g, '').slice(0, 8)
    if (d.length <= 2) return d
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

/* ================================================================
   Type helpers
   ================================================================ */
type Step = 'loading' | 'not-found' | 'identify' | 'login' | 'register' | 'details' | 'terms' | 'summary' | 'result'

/* ================================================================
   Checkout Page
   ================================================================ */
export function CheckoutPage() {
    const { servicoId } = useParams<{ servicoId: string }>()
    const navigate = useNavigate()

    /* ---- Service state ---- */
    const [servico, setServico] = useState<Servico | null>(null)
    const [step, setStep] = useState<Step>('loading')

    /* ---- Identified associate ---- */
    const { associado, setAssociado } = usePortalStore()
    const [isExistingAssociate, setIsExistingAssociate] = useState(false)

    /* ---- Form fields ---- */
    const [identType, setIdentType] = useState<'cpf' | 'cnpj'>('cpf')
    const [cpf, setCpf] = useState('')
    const [cpfError, setCpfError] = useState('')
    const [cpfLoading, setCpfLoading] = useState(false)

    // Login (WhatsApp OTP)
    const [otpSent, setOtpSent] = useState(false)
    const [otp, setOtp] = useState('')
    const [generatedOtp, setGeneratedOtp] = useState('')
    const [otpLoading, setOtpLoading] = useState(false)
    const [loginError, setLoginError] = useState('')

    // Register
    const [nome, setNome] = useState('')
    const [dataNascimento, setDataNascimento] = useState('')
    const [telefone, setTelefone] = useState('')
    const [email, setEmail] = useState('')
    // Address
    const [cep, setCep] = useState('')
    const [logradouro, setLogradouro] = useState('')
    const [numero, setNumero] = useState('')
    const [complemento, setComplemento] = useState('')
    const [bairro, setBairro] = useState('')
    const [cidade, setCidade] = useState('')
    const [uf, setUf] = useState('')
    const [cepLoading, setCepLoading] = useState(false)

    const [regError, setRegError] = useState('')
    const [regLoading, setRegLoading] = useState(false)

    // Terms
    const [termoAceito, setTermoAceito] = useState(false)
    const [scrolledToBottom, setScrolledToBottom] = useState(false)
    const termsRef = useRef<HTMLDivElement>(null)

    // Summary — dia de vencimento da mensalidade
    const [diaVencimento, setDiaVencimento] = useState<10 | 20>(10)

    // Placa (if required)
    const [placa, setPlaca] = useState('')
    const [placaError, setPlacaError] = useState('')

    // Result
    const [submitLoading, setSubmitLoading] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [adesaoUrl, setAdesaoUrl] = useState<string | null>(null)
    const [contratoId, setContratoId] = useState<string | null>(null)
    const [redirectCountdown, setRedirectCountdown] = useState(6)

    // Planos/Adicionais/Cross-sell
    const [selectedPlano, setSelectedPlano] = useState<ServicoPlano | null>(null)
    const [adicionalQtds, setAdicionalQtds] = useState<Record<string, number>>({})
    const [crossSellAccepted, setCrossSellAccepted] = useState<Record<string, boolean>>({})

    /* ---- Load service ---- */
    useEffect(() => {
        if (!servicoId) { setStep('not-found'); return }
        servicoService.getPublicById(servicoId).then(s => {
            if (!s) { setStep('not-found'); return }
            setServico(s)
            // Init plano selection
            const activePlanos = (s.planos || []).filter(p => p.ativo)
            if (activePlanos.length > 0) {
                setSelectedPlano(activePlanos.find(p => p.destaque) || activePlanos[0] || null)
            }
            // Init adicional qtds
            const initQtds: Record<string, number> = {}
            ;(s.adicionais || []).filter(a => a.ativo).forEach(a => { initQtds[a.id] = 0 })
            setAdicionalQtds(initQtds)
            setStep('identify')
        })
    }, [servicoId])

    /* ---- Auto-redirect to portal after success ---- */
    useEffect(() => {
        if (step !== 'result') return
        if (redirectCountdown === 0) {
            navigate('/portal/contratos')
            return
        }
        const timer = setTimeout(() => {
            setRedirectCountdown(c => c - 1)
        }, 1000)
        return () => clearTimeout(timer)
    }, [step, redirectCountdown, navigate])

    /* ---- Branding ---- */
    const branding: CheckoutBranding = useMemo(() => ({
        cor_primaria: (servico?.config?.checkout_branding as CheckoutBranding | undefined)?.cor_primaria || '#0ea5e9',
        cor_fundo: (servico?.config?.checkout_branding as CheckoutBranding | undefined)?.cor_fundo || '#0f172a',
        logo_url: (servico?.config?.checkout_branding as CheckoutBranding | undefined)?.logo_url ?? null,
        logo_contorno: (servico?.config?.checkout_branding as CheckoutBranding | undefined)?.logo_contorno ?? false,
        titulo_hero: (servico?.config?.checkout_branding as CheckoutBranding | undefined)?.titulo_hero ?? null,
    }), [servico])

    /* ---- Adhesion value ---- */
    const cfg = servico?.config_contratacao
    const hasTermo = !!cfg?.termo_aceite
    const hasAdesao = !!cfg?.gerar_adesao

    /* ---- Effective price based on associate status + plano ---- */
    const activePlanos = useMemo(() => (servico?.planos || []).filter(p => p.ativo), [servico])
    const activeAdicionais = useMemo(() => (servico?.adicionais || []).filter(a => a.ativo), [servico])
    const crossSellConfig = servico?.config_contratacao?.cross_sell

    const valorEfetivo = useMemo(() => {
        if (!servico) return 0
        if (selectedPlano) return selectedPlano.valor_mensal
        if (isExistingAssociate && servico.valor_mensal_associado != null)
            return servico.valor_mensal_associado
        if (!isExistingAssociate && associado === null && servico.valor_mensal_nao_associado != null)
            return servico.valor_mensal_nao_associado
        return servico.valor_mensal
    }, [servico, isExistingAssociate, associado, selectedPlano])

    const valorAdicionaisTotal = useMemo(() => {
        let total = 0
        activeAdicionais.forEach(a => {
            const qty = adicionalQtds[a.id] || 0
            const cobrado = Math.max(0, qty - a.incluso_no_plano)
            total += cobrado * a.valor_unitario
        })
        return total
    }, [adicionalQtds, activeAdicionais])

    const valorMensalFinal = valorEfetivo + valorAdicionaisTotal

    const hasSpecialPrice = servico != null && valorEfetivo !== servico.valor_mensal

    const valorAdesao = useMemo(() => {
        if (!hasAdesao || !servico) return 0
        if (cfg?.adesao_pro_rata) return calcProRata(valorEfetivo)
        // Fixed adhesion: pick the correct side based on associate status
        if (isExistingAssociate) {
            // Associate: use valor_adesao_fixo, fallback to valorEfetivo (mensalidade do associado)
            return cfg?.valor_adesao_fixo ?? valorEfetivo
        } else {
            // Non-associate: use valor_adesao_fixo_nao_associado, fallback to valorEfetivo (mensalidade do não-associado)
            return cfg?.valor_adesao_fixo_nao_associado ?? valorEfetivo
        }
    }, [hasAdesao, cfg, servico, valorEfetivo, isExistingAssociate])

    /* ---- Terms scroll ---- */
    useEffect(() => {
        if (step === 'terms' && termsRef.current) {
            const el = termsRef.current
            if (el.scrollHeight <= el.clientHeight + 20) setScrolledToBottom(true)
        }
    }, [step])

    /* ----------------------------------------------------------------
       Step handlers
    ---------------------------------------------------------------- */
    const handleIdentify = async () => {
        const cleanVal = cpf.replace(/\D/g, '')
        const isCpf = identType === 'cpf'
        if (isCpf && cleanVal.length !== 11) { setCpfError('Informe um CPF válido'); return }
        if (!isCpf && cleanVal.length !== 14) { setCpfError('Informe um CNPJ válido'); return }
        
        setCpfError('')
        setCpfLoading(true)
        try {
            const { exists, associado: found } = await associadoService.lookupByCpf(cleanVal)
            if (exists && found) {
                setAssociado(found, cleanVal)
                setIsExistingAssociate(true)
                setStep('login')
            } else {
                setIsExistingAssociate(false)
                setStep('register')
            }
        } catch {
            setCpfError('Erro ao verificar CPF. Tente novamente.')
        } finally {
            setCpfLoading(false)
        }
    }

    const sendOtp = async () => {
        setLoginError('')
        setOtpLoading(true)
        try {
            const cleanCpf = cpf.replace(/\D/g, '')
            const { data, error } = await supabase.functions.invoke('portal-auth', {
                body: { action: 'send', cpf: cleanCpf }
            })

            if (error) throw error
            if (data?.error) throw new Error(data.error)

            setOtpSent(true)

            // Em ambiente de desenvolvimento, revelamos o código para testes
            if (import.meta.env.DEV && data?.dev_code) {
                setLoginError(`[DEV] Código enviado: ${data.dev_code}`)
                // Mantemos o state the generatedOtp em ambiente local apenas como fallback
                // de segurança, mas a validação final real ocorre no back-end
                setGeneratedOtp(data.dev_code)
            }
        } catch (err: any) {
            console.error('sendOtp error:', err)
            // Lida com erro customizado 429 ou 400 da function
            if (err.context && err.context.status === 429) {
                setLoginError('Aguarde 1 minuto antes de pedir outro código.')
            } else {
                setLoginError(err.message || 'Falha ao enviar o código. Tente novamente.')
            }
        } finally {
            setOtpLoading(false)
        }
    }

    const verifyOtp = async () => {
        if (!otp || otp.length < 5) return

        setLoginError('')
        // setOtpLoading(true) /* Reusing otpLoading state here to disable button */
        setOtpLoading(true)

        try {
            const cleanCpf = cpf.replace(/\D/g, '')
            const { data, error } = await supabase.functions.invoke('portal-auth', {
                body: { action: 'verify', cpf: cleanCpf, codigo: otp }
            })

            if (error) {
                // Erro 401 ou 400
                const errorMsg = await error.context?.json().catch(() => null)
                throw new Error(errorMsg?.error || 'Código inválido ou expirado.')
            }

            if (data?.error) throw new Error(data.error)

            // Successo! Associado verificado.
            // Opcional: Atualizar associado com os dados frescos vindos da function
            if (data.associado) {
                setAssociado({ ...associado!, ...data.associado }, cleanCpf)
            }

            setStep('details')
        } catch (err: any) {
            console.error('verifyOtp error:', err)
            setLoginError(err.message || 'Código incorreto. Verifique e tente novamente.')
        } finally {
            setOtpLoading(false)
        }
    }
    
    const handleCepChange = (val: string) => {
        const clean = val.replace(/\D/g, '').slice(0, 8)
        const masked = maskCep(clean)
        setCep(masked)
    }

    useEffect(() => {
        const clean = cep.replace(/\D/g, '')
        if (clean.length === 8) {
            const trigger = async () => {
                setCepLoading(true)
                try {
                    // Fetch through our Netlify proxy to bypass CSP
                    const res = await fetch(`/api/cep/${clean}/json/`)
                    const data = await res.json()
                    if (data.erro) throw new Error()
                    setLogradouro(data.logradouro)
                    setBairro(data.bairro)
                    setCidade(data.localidade)
                    setUf(data.uf)
                } catch (err) {
                    console.error('CEP error:', err)
                } finally {
                    setCepLoading(false)
                }
            }
            trigger()
        }
    }, [cep])

    const handleRegister = async () => {
        if (!servico) return
        if (!nome.trim()) { setRegError('Informe seu nome completo'); return }
        if (identType === 'cpf' && !dataNascimento) { setRegError('Informe sua data de nascimento'); return }
        if (!telefone || telefone.replace(/\D/g, '').length < 10) { setRegError('Informe um telefone válido'); return }
        if (!cep || !logradouro || !cidade) { setRegError('Informe os dados de endereço'); return }

        setRegError('')
        setRegLoading(true)
        try {
            const cleanVal = cpf.replace(/\D/g, '')

            // Convert DD/MM/YYYY to YYYY-MM-DD
            let formattedBirthDate = dataNascimento
            if (dataNascimento && dataNascimento.includes('/')) {
                const [d, m, y] = dataNascimento.split('/')
                if (d && m && y && y.length === 4) {
                    formattedBirthDate = `${y}-${m}-${d}`
                }
            }

            const created = await associadoService.create({
                tenant_id: servico.tenant_id,
                cpf: cleanVal,
                nome: nome.trim(),
                data_nascimento: formattedBirthDate || undefined,
                telefone: telefone.replace(/\D/g, ''),
                email: email.trim() || undefined,
                endereco: {
                    cep: cep.replace(/\D/g, ''),
                    rua: logradouro.trim(),
                    numero: numero.trim(),
                    bairro: bairro.trim(),
                    cidade: cidade.trim(),
                    uf: uf.trim().toUpperCase(),
                    complemento: complemento.trim() || undefined,
                }
            })
            setAssociado(created, cleanVal)
            setStep('details')
        } catch {
            setRegError('Erro ao criar cadastro. Tente novamente.')
        } finally {
            setRegLoading(false)
        }
    }

    const handleDetailsNext = () => {
        if (servico?.exige_veiculo && (!placa || placa.replace(/\D/g, '').length < 7 && placa.length < 7)) {
            // Very basic plate validation (3 letters + 4 numbers or Mercosul)
            const cleanPlaca = placa.replace(/[^A-Za-z0-9]/g, '')
            if (cleanPlaca.length < 7) {
                setPlacaError('Informe uma placa válida')
                return
            }
        }
        setPlacaError('')
        if (hasTermo) setStep('terms')
        else setStep('summary')
    }

    const handleTermsNext = () => setStep('summary')
    
    const handleConfirm = async () => {
        if (!associado || !servico) return
        setSubmitLoading(true)
        setSubmitError('')
        try {
            // Geração de Hash de Assinatura Digital (SHA-256)
            const timestamp = new Date().toISOString()
            const userAgent = navigator.userAgent
            const documentContent = servico.config_contratacao?.termo_aceite || 'Termo de adesão padrão.'
            const manifestoStr = `${associado.cpf}|${timestamp}|${documentContent}`
            
            const msgUint8 = new TextEncoder().encode(manifestoStr)
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

            const { data, error } = await supabase.functions.invoke('checkout-contratar', {
                body: {
                    associado_id: associado.id,
                    servico_id: servico.id,
                    tenant_id: servico.tenant_id,
                    dia_vencimento: diaVencimento,
                    valor_mensal: valorMensalFinal,
                    valor_adesao: valorAdesao,
                    adesao_pro_rata: cfg?.adesao_pro_rata ?? false,
                    termo_aceite_em: timestamp,
                    placa: (placa && placa.trim().length >= 7) ? placa.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : null,
                    ...(selectedPlano ? { plano_id: selectedPlano.id, plano_nome: selectedPlano.nome } : {}),
                    ...(Object.keys(adicionalQtds).some(k => adicionalQtds[k]! > 0) ? {
                        adicionais: activeAdicionais
                            .filter(a => (adicionalQtds[a.id] || 0) > 0)
                            .map(a => ({
                                adicional_id: a.id,
                                nome: a.nome,
                                quantidade: adicionalQtds[a.id],
                                valor_unitario: a.valor_unitario,
                            }))
                    } : {}),
                    assinatura_digital: {
                        hash: hashHex,
                        timestamp,
                        user_agent: userAgent,
                        documento_base: documentContent
                    }
                },
            })
            if (error) throw error
            setAdesaoUrl(data?.adesao_url ?? null)
            if (data?.contrato_id) setContratoId(data.contrato_id)
            setStep('result')
        } catch (err: any) {
            let msg = err.message || 'Erro ao processar contratação.'
            try {
                if (err.context && typeof err.context.json === 'function') {
                    const parsed = await err.context.json()
                    msg = parsed.error || msg
                } else if (err.context?.error) {
                    msg = err.context.error
                } else if (typeof err.context === 'string') {
                    const parsed = JSON.parse(err.context)
                    if (parsed.error) msg = parsed.error
                }
            } catch (e) {
                // Ignore parse errors
            }
            console.error('RAW ERROR FROM EDGE FUNCTION:', err)
            console.error('CONTEXT:', err.context)
            if (msg.includes('non-2xx')) {
                msg = 'Ocorreu um erro no servidor ao processar sua solicitação.'
            }
            setSubmitError(msg)
        } finally {
            setSubmitLoading(false)
        }
    }

    /* ================================================================
       Render
       ================================================================ */
    const primHex = branding.cor_primaria
    const bgHex = branding.cor_fundo

    return (
        <div className="min-h-screen w-full flex flex-col font-sans selection:bg-white/20 overflow-x-hidden" style={{ backgroundColor: bgHex }}>
            {/* ---- Dynamic CSS vars ---- */}
            <style>{`
                :root {
                    --chk-primary: ${primHex};
                    --chk-bg: ${bgHex};
                }
                .chk-btn {
                    background-color: ${primHex};
                    color: #fff;
                }
                .chk-btn:hover { filter: brightness(1.1); }
                .chk-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .chk-ring:focus { outline: 2px solid ${primHex}; outline-offset: 2px; }
                .chk-text { color: ${primHex}; }
                .chk-border { border-color: ${primHex}; }
            `}</style>

            {/* ---- Background blobs ---- */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full blur-3xl opacity-20"
                    style={{ backgroundColor: primHex }} />
                <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full blur-3xl opacity-10"
                    style={{ backgroundColor: primHex }} />
            </div>

            {/* ---- Header (minimal, no logo) ---- */}
            <header className="relative z-10 px-6 py-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center opacity-40"
                    style={{ backgroundColor: primHex }}>
                    <Shield size={15} className="text-white" />
                </div>
            </header>

            {/* ---- Main ---- */}
            <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-8">

                {/* Logo sempre visível em todas as etapas (Top) */}
                {servico && step !== 'loading' && step !== 'not-found' && (
                    <div className="flex justify-center mb-6">
                        {branding.logo_url ? (
                            <img
                                src={branding.logo_url}
                                alt="Logo"
                                className="h-[90px] object-contain"
                                style={branding.logo_contorno ? {
                                    padding: '10px 14px',
                                    borderRadius: '16px',
                                    background: '#ffffff',
                                    border: '2px solid rgba(255,255,255,0.8)',
                                    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                                } : undefined}
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                                style={{ backgroundColor: primHex }}>
                                <Shield size={32} className="text-white" />
                            </div>
                        )}
                    </div>
                )}

                <div className="w-full max-w-md">
                    {/* Hero title (shown on identify step) */}
                    {step === 'identify' && servico && (
                        <div className="text-center mb-8">
                            <p className="text-white/50 text-sm uppercase tracking-widest mb-2">Contratação Online</p>
                            <h1 className="text-3xl font-bold text-white leading-tight">
                                {branding.titulo_hero || servico.nome}
                            </h1>
                            <p className="text-white/60 mt-2 text-sm max-w-xs mx-auto">{servico.descricao || ''}</p>
                            <div className="mt-4 flex flex-col items-center gap-1">
                                {hasSpecialPrice && (
                                    <p className="text-white/30 text-base line-through">{formatCurrency(servico.valor_mensal)}</p>
                                )}
                                <p className="text-4xl font-extrabold" style={{ color: primHex }}>
                                    {formatCurrency(valorEfetivo)}
                                    <span className="text-base font-normal text-white/40 ml-1">/mês</span>
                                </p>
                                {hasSpecialPrice && isExistingAssociate && (
                                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                        Preço Associado
                                    </span>
                                )}
                                {hasSpecialPrice && !isExistingAssociate && (
                                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                        Preço Padrão
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Card */}
                    <div className="rounded-3xl shadow-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.12)' }}>

                        {/* ===== LOADING ===== */}
                        {step === 'loading' && (
                            <div className="p-10 flex flex-col items-center gap-4 text-white">
                                <Loader2 size={32} className="animate-spin" style={{ color: primHex }} />
                                <p className="text-white/60">Carregando serviço...</p>
                            </div>
                        )}

                        {/* ===== NOT FOUND ===== */}
                        {step === 'not-found' && (
                            <div className="p-10 flex flex-col items-center gap-3 text-center">
                                <AlertCircle size={40} className="text-red-400" />
                                <h2 className="text-xl font-bold text-white">Serviço não encontrado</h2>
                                <p className="text-white/50 text-sm">Este link de checkout é inválido ou o serviço foi desativado.</p>
                            </div>
                        )}

                        {/* ===== IDENTIFY ===== */}
                        {step === 'identify' && (
                            <div className="p-6 space-y-6">
                                <div>
                                    <h2 className="text-lg font-bold text-white">Identificação</h2>
                                    <p className="text-sm text-white/50 mt-0.5">Informe seus dados para começar</p>
                                </div>

                                {/* CPF/CNPJ Toggle */}
                                <div className="p-1 bg-white/5 rounded-2xl flex gap-1">
                                    {(['cpf', 'cnpj'] as const).map(type => (
                                        <button
                                            key={type}
                                            onClick={() => { setIdentType(type); setCpf(''); setCpfError('') }}
                                            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wider
                                                ${identType === type ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
                                        >
                                            {type === 'cpf' ? 'Pessoa Física' : 'Empresa'}
                                        </button>
                                    ))}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wide">
                                        {identType === 'cpf' ? 'CPF' : 'CNPJ'}
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={cpf}
                                        onChange={e => setCpf(identType === 'cpf' ? maskCpf(e.target.value) : maskCnpj(e.target.value))}
                                        placeholder={identType === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                                        className="chk-ring w-full px-4 py-3.5 rounded-xl bg-white/10 border border-white/15 text-white placeholder:text-white/30 text-lg focus:outline-none"
                                        onKeyDown={e => e.key === 'Enter' && handleIdentify()}
                                    />
                                    {cpfError && <p className="text-red-400 text-xs mt-1.5">{cpfError}</p>}
                                </div>
                                <button
                                    onClick={handleIdentify}
                                    disabled={cpfLoading}
                                    className="chk-btn w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-xl shadow-black/20"
                                >
                                    {cpfLoading ? <Loader2 size={18} className="animate-spin" /> : <>Continuar <ArrowRight size={20} /></>}
                                </button>
                            </div>
                        )}

                        {/* ===== LOGIN (Email/SMS OTP) ===== */}
                        {step === 'login' && associado && (
                            <div className="p-6 space-y-5">
                                <div>
                                    <h2 className="text-lg font-bold text-white">Bem-vindo(a) de volta!</h2>
                                    <p className="text-sm text-white/50 mt-0.5">
                                        Encontramos o cadastro de <strong className="text-white/80">{associado.nome}</strong>
                                    </p>
                                </div>

                                {!otpSent ? (
                                    /* ---- Phase 1: send code ---- */
                                    <>
                                        <div className="rounded-xl p-4 bg-white/5 border border-white/10 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                                style={{ backgroundColor: `${primHex}22` }}>
                                                <Phone size={18} style={{ color: primHex }} />
                                            </div>
                                            <div>
                                                <p className="text-xs text-white/40 mb-0.5">Número cadastrado</p>
                                                <p className="text-white font-mono font-semibold">
                                                    {associado.telefone
                                                        ? `(${associado.telefone.slice(0, 2)}) ${'*'.repeat(5)}-${associado.telefone.slice(-4)}`
                                                        : 'Não informado'}
                                                </p>
                                            </div>
                                        </div>

                                        <p className="text-xs text-white/40 text-center leading-relaxed">
                                            Vamos confirmar sua identidade.<br />
                                            Enviaremos um código de 6 dígitos para seu email e celular.
                                        </p>

                                        {loginError && (
                                            <div className={`flex items-center gap-2 p-3 rounded-xl text-xs ${loginError.startsWith('[DEV]')
                                                ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400'
                                                : 'bg-red-500/10 border border-red-500/20 text-red-400'
                                                }`}>
                                                <AlertCircle size={13} className="flex-shrink-0" /> {loginError}
                                            </div>
                                        )}

                                        <div className="flex gap-3">
                                            <button onClick={() => setStep('identify')}
                                                className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-all flex items-center justify-center gap-2">
                                                <ArrowLeft size={16} /> Voltar
                                            </button>
                                            <button
                                                onClick={sendOtp}
                                                disabled={otpLoading}
                                                className="chk-btn flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                                            >
                                                {otpLoading
                                                    ? <Loader2 size={18} className="animate-spin" />
                                                    : <><MessageCircle size={18} /> Enviar código</>}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    /* ---- Phase 2: enter code ---- */
                                    <>
                                        <div className="rounded-xl p-4 bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                                            <MessageCircle size={20} className="text-green-400 flex-shrink-0" />
                                            <p className="text-sm text-green-300">
                                                Código enviado! Verifique seu email e celular.
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-white/60 mb-1.5">Código de 6 dígitos</label>
                                            <div className="relative">
                                                <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={6}
                                                    value={otp}
                                                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                    placeholder="000000"
                                                    className="chk-ring w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder:text-white/30 text-xl tracking-[0.4em] font-mono text-center focus:outline-none"
                                                    onKeyDown={e => e.key === 'Enter' && verifyOtp()}
                                                />
                                            </div>
                                            {loginError && !loginError.startsWith('[DEV]') && (
                                                <p className="text-red-400 text-xs mt-1.5">{loginError}</p>
                                            )}
                                            {loginError.startsWith('[DEV]') && (
                                                <p className="text-yellow-400 text-xs mt-1.5">{loginError}</p>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => { setOtpSent(false); setOtp(''); setLoginError(''); setGeneratedOtp('') }}
                                            className="text-xs text-white/30 hover:text-white/60 underline text-center w-full transition-colors"
                                        >
                                            Não recebi o código — reenviar
                                        </button>

                                        <div className="flex gap-3">
                                            <button onClick={() => setStep('identify')}
                                                className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-all flex items-center justify-center gap-2">
                                                <ArrowLeft size={16} /> Voltar
                                            </button>
                                            <button
                                                onClick={verifyOtp}
                                                disabled={otp.length !== 6}
                                                className="chk-btn flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                                            >
                                                <CheckCircle2 size={18} /> Verificar
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* ===== REGISTER ===== */}
                        {step === 'register' && (
                            <div className="p-6 space-y-4">
                                <div>
                                    <h2 className="text-lg font-bold text-white">CRIAR CADASTRO</h2>
                                    <p className="text-sm text-white/50 mt-0.5">
                                        {identType === 'cpf' ? 'CPF' : 'CNPJ'} não encontrado. Preencha seus dados para continuar.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">
                                                {identType === 'cpf' ? 'Nome Completo' : 'Razão Social'}
                                            </label>
                                            <div className="relative">
                                                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                                                <input type="text" value={nome} onChange={e => setNome(e.target.value)}
                                                    placeholder={identType === 'cpf' ? 'Seu nome completo' : 'Nome da empresa'}
                                                    className="chk-ring w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            {identType === 'cpf' && (
                                                <div>
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">Nascimento</label>
                                                    <div className="relative">
                                                        <Calendar size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                                                        <input 
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={dataNascimento} 
                                                            onChange={e => setDataNascimento(maskDate(e.target.value))}
                                                            placeholder="DD/MM/AAAA"
                                                            className="chk-ring w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                    </div>
                                                </div>
                                            )}
                                            <div className={identType === 'cnpj' ? 'col-span-2' : ''}>
                                                <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">WhatsApp</label>
                                                <div className="relative">
                                                    <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                                                    <input type="tel" 
                                                        inputMode="numeric"
                                                        value={telefone} onChange={e => setTelefone(maskPhone(e.target.value))}
                                                        placeholder="(00) 00000-0000"
                                                        className="chk-ring w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">E-mail</label>
                                            <div className="relative">
                                                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                                                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                                    placeholder="seu@email.com"
                                                    className="chk-ring w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-white/5">
                                            <p className="text-[10px] font-bold text-white/40 mb-3 uppercase tracking-wider">Endereço de Cobrança</p>
                                            
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="col-span-1">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">CEP</label>
                                                    <div className="relative">
                                                        <input type="text" 
                                                            inputMode="numeric"
                                                            value={cep} onChange={e => handleCepChange(e.target.value)}
                                                            placeholder="00000-000"
                                                            className="chk-ring w-full px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                        {cepLoading && <Loader2 size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-white/30" />}
                                                    </div>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">Rua / Logradouro</label>
                                                    <input type="text" value={logradouro} onChange={e => setLogradouro(e.target.value)}
                                                        className="chk-ring w-full px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-4 gap-4 mt-4">
                                                <div className="col-span-1">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">Nº</label>
                                                    <input type="text" value={numero} onChange={e => setNumero(e.target.value)}
                                                        className="chk-ring w-full px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                </div>
                                                <div className="col-span-3">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">Complemento</label>
                                                    <input type="text" value={complemento} onChange={e => setComplemento(e.target.value)}
                                                        className="chk-ring w-full px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-12 gap-4 mt-4">
                                                <div className="col-span-5">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">Bairro</label>
                                                    <input type="text" value={bairro} onChange={e => setBairro(e.target.value)}
                                                        className="chk-ring w-full px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                </div>
                                                <div className="col-span-4">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase">Cidade</label>
                                                    <input type="text" value={cidade} onChange={e => setCidade(e.target.value)}
                                                        className="chk-ring w-full px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none" />
                                                </div>
                                                <div className="col-span-3">
                                                    <label className="block text-[10px] font-bold text-white/40 mb-1.5 uppercase text-center">UF</label>
                                                    <input type="text" value={uf} onChange={e => setUf(e.target.value.toUpperCase().slice(0, 2))}
                                                        placeholder="UF"
                                                        className="chk-ring w-full px-2 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm text-center focus:outline-none" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {regError && (
                                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                                        <AlertCircle size={14} /> {regError}
                                    </div>
                                )}

                                <div className="flex gap-3 pt-4 border-t border-white/5">
                                    <button onClick={() => setStep('identify')} className="flex-1 py-3.5 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-all flex items-center justify-center gap-2">
                                        <ArrowLeft size={16} /> Voltar
                                    </button>
                                    <button
                                        onClick={handleRegister}
                                        disabled={regLoading}
                                        className="chk-btn flex-1 py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
                                    >
                                        {regLoading ? <Loader2 size={18} className="animate-spin" /> : <>Próximo <ArrowRight size={18} /></>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ===== DETAILS ===== */}
                        {step === 'details' && servico && (
                            <div className="p-6 space-y-4">
                                <div>
                                    <h2 className="text-lg font-bold text-white">{servico.nome}</h2>
                                    <p className="text-sm text-white/50 mt-0.5">Detalhes do plano</p>
                                </div>

                                <p className="text-sm text-white/70 leading-relaxed">{servico.descricao || 'Sem descrição disponível.'}</p>

                                <div className="space-y-2">
                                    {[
                                        {
                                            label: 'Mensalidade',
                                            value: (
                                                <div className="flex flex-col items-end gap-0.5">
                                                    {hasSpecialPrice && (
                                                        <span className="text-white/30 text-xs line-through">{formatCurrency(servico.valor_mensal)}</span>
                                                    )}
                                                    <span className="text-xl font-bold" style={{ color: primHex }}>{formatCurrency(valorEfetivo)}</span>
                                                    {hasSpecialPrice && isExistingAssociate && (
                                                        <span className="text-[10px] font-semibold text-emerald-400">Preço Associado</span>
                                                    )}
                                                </div>
                                            ),
                                        },
                                        { label: 'Carência', value: servico.carencia_dias > 0 ? `${servico.carencia_dias} dias` : 'Sem carência' },
                                        ...(hasAdesao ? [{ label: 'Taxa de adesão', value: formatCurrency(valorAdesao), warn: true }] : []),
                                    ].map(({ label, value, warn }) => (
                                        <div key={label} className={`flex items-center justify-between px-4 py-3 rounded-xl ${warn ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-white/5'}`}>
                                            <span className="text-sm text-white/60">{label}</span>
                                            <span className={`font-semibold text-sm ${warn ? 'text-amber-400' : 'text-white'}`}>{value}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Sempre Juntos Points Badge */}
                                {!!servico.pontos_adesao && servico.pontos_adesao > 0 && (
                                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-rose-500/15 border border-rose-500/25">
                                        <span className="text-sm text-rose-300 font-bold flex items-center gap-1.5">
                                            <Heart size={14} className="fill-current" /> Bônus Sempre Juntos
                                        </span>
                                        <span className="font-black text-rose-400">+{servico.pontos_adesao} PONTOS</span>
                                    </div>
                                )}

                                {servico.exige_veiculo && (
                                    <div className="pt-2">
                                        <label className="block text-xs font-medium text-white/60 mb-2 uppercase tracking-wide">Placa do Veículo (Obrigatório)</label>
                                        <input
                                            type="text"
                                            value={placa}
                                            onChange={e => setPlaca(maskPlaca(e.target.value))}
                                            placeholder="ABC-1234 ou ABC1D23"
                                            className={`chk-ring w-full px-4 py-3.5 rounded-xl bg-white/10 border text-white placeholder:text-white/30 text-lg focus:outline-none uppercase ${placaError ? 'border-red-500' : 'border-white/15'}`}
                                        />
                                        {placaError && <p className="text-red-400 text-xs mt-1.5">{placaError}</p>}
                                    </div>
                                )}

                                {cfg?.ativacao_via_api && (
                                    <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                        <AlertCircle size={15} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-blue-300">A ativação pode levar até 24h após a confirmação do pagamento.</p>
                                    </div>
                                )}

                                {/* Plan Selection (inline in details) */}
                                {activePlanos.length > 1 && (
                                    <div className="space-y-3 pt-2">
                                        <div className="flex items-center gap-2 text-sm text-white/60">
                                            <Layers size={15} className="text-indigo-400" />
                                            <span className="text-xs font-bold uppercase tracking-wider">Escolha seu plano</span>
                                        </div>
                                        {activePlanos.map(p => {
                                            const isSelected = selectedPlano?.id === p.id
                                            return (
                                                <button key={p.id} onClick={() => setSelectedPlano(p)}
                                                    className={`w-full text-left p-4 rounded-xl border transition-all relative
                                                        ${isSelected ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                                                    {p.destaque && (
                                                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[9px] font-black flex items-center gap-1">
                                                            <Star size={8} fill="currentColor" /> Recomendado
                                                        </span>
                                                    )}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                                                                ${isSelected ? 'border-indigo-400 bg-indigo-500' : 'border-white/30'}`}>
                                                                {isSelected && <CheckCircle2 size={10} className="text-white" />}
                                                            </div>
                                                            <span className="font-bold text-white">{p.nome}</span>
                                                        </div>
                                                        <span className="font-black text-white">
                                                            {formatCurrency(p.valor_mensal)}<span className="text-xs text-white/40">/mês</span>
                                                        </span>
                                                    </div>
                                                    {p.inclusoes && p.inclusoes.length > 0 && (
                                                        <div className="ml-8 mt-2 space-y-1">
                                                            {p.inclusoes.map((inc, i) => (
                                                                <div key={i} className="flex items-center gap-1.5 text-xs text-white/50">
                                                                    <CheckCircle2 size={10} className="text-emerald-400 flex-shrink-0" />
                                                                    <span>{inc.titulo}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </button>
                                            )
                                        })}
                                    </div>
                                )}

                                {/* Adicionais (inline in details) */}
                                {activeAdicionais.length > 0 && (
                                    <div className="space-y-3 pt-2">
                                        <div className="flex items-center gap-2 text-sm text-white/60">
                                            <ListPlus size={15} className="text-teal-400" />
                                            <span className="text-xs font-bold uppercase tracking-wider">Personalize</span>
                                        </div>
                                        {activeAdicionais.map(a => {
                                            const qty = adicionalQtds[a.id] || 0
                                            const cobrado = Math.max(0, qty - a.incluso_no_plano)
                                            const custoExtra = cobrado * a.valor_unitario
                                            return (
                                                <div key={a.id} className="p-3 rounded-xl bg-white/5 border border-white/10 space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1 min-w-0 pr-3">
                                                            <p className="text-sm font-bold text-white">{a.nome}</p>
                                                            {a.descricao && <p className="text-[11px] text-white/40 mt-0.5">{a.descricao}</p>}
                                                            <div className="flex gap-1.5 mt-1">
                                                                <span className="text-[9px] font-bold text-white/30 bg-white/5 px-1.5 py-0.5 rounded-full">
                                                                    {formatCurrency(a.valor_unitario)}/un
                                                                </span>
                                                                {a.incluso_no_plano > 0 && (
                                                                    <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">
                                                                        {a.incluso_no_plano} incluso(s)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {a.tipo === 'toggle' ? (
                                                            <button onClick={() => setAdicionalQtds(prev => ({ ...prev, [a.id]: qty > 0 ? 0 : 1 }))}
                                                                className={`w-12 h-7 rounded-full transition-all flex items-center px-0.5
                                                                    ${qty > 0 ? 'justify-end' : 'justify-start'}`}
                                                                style={{ backgroundColor: qty > 0 ? primHex : 'rgba(255,255,255,0.15)' }}>
                                                                <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                                                            </button>
                                                        ) : (
                                                            <div className="flex items-center gap-1">
                                                                <button onClick={() => setAdicionalQtds(prev => ({ ...prev, [a.id]: Math.max(0, qty - 1) }))}
                                                                    disabled={qty <= 0}
                                                                    className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 disabled:opacity-30">
                                                                    <Minus size={12} />
                                                                </button>
                                                                <span className="w-6 text-center font-bold text-white text-sm">{qty}</span>
                                                                <button onClick={() => setAdicionalQtds(prev => ({ ...prev, [a.id]: Math.min(a.max_quantidade, qty + 1) }))}
                                                                    disabled={qty >= a.max_quantidade}
                                                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white hover:opacity-80 disabled:opacity-30"
                                                                    style={{ backgroundColor: primHex }}>
                                                                    <Plus size={12} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {custoExtra > 0 && (
                                                        <div className="text-right text-xs font-bold" style={{ color: primHex }}>
                                                            +{formatCurrency(custoExtra)}/mês
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}

                                {/* Valor mensal final (se há adicionais ou plano) */}
                                {(activePlanos.length > 1 || activeAdicionais.length > 0) && (
                                    <div className="flex items-center justify-between px-4 py-3 rounded-xl border" style={{ borderColor: primHex + '40', backgroundColor: primHex + '15' }}>
                                        <span className="text-sm text-white/70 font-bold">Mensalidade Total</span>
                                        <span className="font-black text-xl text-white">{formatCurrency(valorMensalFinal)}</span>
                                    </div>
                                )}

                                <div className="flex gap-3 pt-1">
                                    <button onClick={() => setStep(isExistingAssociate ? 'login' : 'register')}
                                        className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-all flex items-center justify-center gap-2">
                                        <ArrowLeft size={16} /> Voltar
                                    </button>
                                    <button
                                        onClick={handleDetailsNext}
                                        className="chk-btn flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                                    >
                                        Próximo <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ===== TERMS ===== */}
                        {step === 'terms' && servico && (
                            <div className="p-6 space-y-4">
                                <div className="flex items-center gap-2">
                                    <ScrollText size={20} style={{ color: primHex }} />
                                    <h2 className="text-lg font-bold text-white">Termo de Aceite</h2>
                                </div>

                                <div
                                    ref={termsRef}
                                    onScroll={e => {
                                        const el = e.currentTarget
                                        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolledToBottom(true)
                                    }}
                                    className="h-56 overflow-y-auto rounded-xl p-4 text-sm text-white/70 leading-relaxed whitespace-pre-wrap font-mono text-xs"
                                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                                >
                                    {cfg?.termo_aceite}
                                </div>

                                {!scrolledToBottom && (
                                    <p className="text-xs text-center text-white/30 animate-pulse">Role até o final ↓</p>
                                )}

                                <label className={`flex items-start gap-3 p-4 rounded-xl border transition-all cursor-pointer
                                    ${termoAceito ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10'}
                                    ${!scrolledToBottom ? 'opacity-40 pointer-events-none' : ''}`}>
                                    <input type="checkbox" checked={termoAceito} onChange={e => setTermoAceito(e.target.checked)}
                                        disabled={!scrolledToBottom}
                                        className="w-4 h-4 mt-0.5 rounded" />
                                    <div>
                                        <p className="text-sm font-medium text-white">Li e aceito os termos e condições</p>
                                        <p className="text-xs text-white/40 mt-0.5">Declaro estar ciente de todas as condições.</p>
                                    </div>
                                </label>

                                <div className="flex gap-3">
                                    <button onClick={() => setStep('details')}
                                        className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-all flex items-center justify-center gap-2">
                                        <ArrowLeft size={16} /> Voltar
                                    </button>
                                    <button
                                        onClick={handleTermsNext}
                                        disabled={!termoAceito}
                                        className="chk-btn flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                                    >
                                        Aceitar <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ===== SUMMARY ===== */}
                        {step === 'summary' && servico && associado && (
                            <div className="p-6 space-y-4">
                                <div>
                                    <h2 className="text-lg font-bold text-white">Confirmação</h2>
                                    <p className="text-sm text-white/50 mt-0.5">Revise e confirme</p>
                                </div>

                                <div className="rounded-xl overflow-hidden divide-y" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                                    {[
                                        { label: 'Titular', value: associado.nome },
                                        { label: 'CPF', value: cpf },
                                        { label: 'Serviço', value: servico.nome },
                                        ...(selectedPlano ? [{ label: 'Plano', value: `${selectedPlano.nome} — ${formatCurrency(selectedPlano.valor_mensal)}` }] : []),
                                        ...(servico.exige_veiculo ? [{ label: 'Placa', value: placa.toUpperCase() }] : []),
                                        { label: 'Mensalidade', value: formatCurrency(valorMensalFinal) },
                                        { label: 'Vencimento', value: `Dia ${diaVencimento} de cada mês` },
                                        ...(hasAdesao ? [{ label: cfg?.adesao_pro_rata ? 'Adesão (pro-rata)' : 'Taxa de adesão', value: formatCurrency(valorAdesao), warn: true }] : []),
                                    ].map(({ label, value, warn }) => (
                                        <div key={label} className={`flex items-center justify-between p-3.5 ${warn ? 'bg-amber-500/10' : 'bg-white/5'}`}>
                                            <span className="text-sm text-white/50">{label}</span>
                                            <span className={`font-semibold text-sm ${warn ? 'text-amber-400' : 'text-white'}`}>{value}</span>
                                        </div>
                                    ))}
                                    {/* Adicionais no resumo */}
                                    {activeAdicionais.filter(a => (adicionalQtds[a.id] || 0) > 0).map(a => {
                                        const qty = adicionalQtds[a.id] || 0
                                        const cobrado = Math.max(0, qty - a.incluso_no_plano)
                                        return (
                                            <div key={a.id} className="flex items-center justify-between p-3.5 bg-teal-500/5">
                                                <span className="text-sm text-teal-300 flex items-center gap-1.5">
                                                    <ListPlus size={12} /> {a.nome} ×{qty}
                                                </span>
                                                <span className="font-bold text-teal-300 text-sm">
                                                    {cobrado > 0 ? `+${formatCurrency(cobrado * a.valor_unitario)}` : 'Incluso'}
                                                </span>
                                            </div>
                                        )
                                    })}
                                    {!!servico.pontos_adesao && servico.pontos_adesao > 0 && (
                                        <div className="flex items-center justify-between p-3.5 bg-rose-500/10">
                                            <span className="text-sm text-rose-300 flex items-center gap-1.5">
                                                <Heart size={14} className="fill-current" /> Sempre Juntos
                                            </span>
                                            <span className="font-black text-rose-400 text-sm">+{servico.pontos_adesao} PTS</span>
                                        </div>
                                    )}
                                </div>

                                {/* Seletor dia de vencimento */}
                                <div>
                                    <p className="text-xs text-white/50 mb-2">Escolha o dia de vencimento da mensalidade</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {([10, 20] as const).map(dia => (
                                            <button
                                                key={dia}
                                                type="button"
                                                onClick={() => setDiaVencimento(dia)}
                                                className={`py-3 rounded-xl font-semibold text-sm border transition-all ${diaVencimento === dia
                                                    ? 'text-white border-transparent'
                                                    : 'bg-white/5 border-white/15 text-white/60 hover:bg-white/10'
                                                    }`}
                                                style={diaVencimento === dia ? { backgroundColor: primHex, borderColor: primHex } : {}}
                                            >
                                                Dia {dia}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-white/30 mt-1.5">A primeira mensalidade vence dia {diaVencimento} do mês seguinte</p>
                                </div>

                                {hasAdesao && (
                                    <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                        <AlertCircle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-amber-300">
                                            Uma cobrança de adesão de <strong>{formatCurrency(valorAdesao)}</strong> vencerá <strong>amanhã</strong>.{' '}
                                            O serviço será ativado após o pagamento. Sem pagamento em 5 dias o contrato é cancelado automaticamente.
                                        </p>
                                    </div>
                                )}

                                {submitError && (
                                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                                        <AlertCircle size={14} /> {submitError}
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <button onClick={() => setStep(hasTermo ? 'terms' : 'details')}
                                        className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 transition-all flex items-center justify-center gap-2">
                                        <ArrowLeft size={16} /> Voltar
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        disabled={submitLoading}
                                        className="chk-btn flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                                    >
                                        {submitLoading ? <Loader2 size={18} className="animate-spin" /> : <><CheckCircle2 size={18} /> Contratar</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ===== RESULT ===== */}
                        {step === 'result' && servico && (
                            <div className="p-8 flex flex-col items-center text-center gap-5">
                                <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primHex}22` }}>
                                    <CheckCircle2 size={44} style={{ color: primHex }} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">
                                        {hasAdesao ? 'Adesão Gerada!' : 'Contratação Concluída!'}
                                    </h2>
                                    <p className="text-white/50 mt-2 text-sm">
                                        {hasAdesao
                                            ? `“${servico.nome}” será ativado após o pagamento da adesão.`
                                            : `“${servico.nome}” foi ativado com sucesso!`}
                                    </p>
                                </div>

                                {hasAdesao && (
                                    <div className="w-full rounded-xl p-4 bg-amber-500/10 border border-amber-500/20 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-amber-300">Valor da adesão</span>
                                            <span className="text-xl font-bold text-amber-400">{formatCurrency(valorAdesao)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-amber-300/70">
                                            <span>Vencimento</span>
                                            <span>Amanhã</span>
                                        </div>
                                        {adesaoUrl ? (
                                            <a
                                                href={adesaoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm text-white transition-all hover:brightness-110"
                                                style={{ backgroundColor: primHex }}
                                            >
                                                <ExternalLink size={16} /> Pagar Adesão
                                            </a>
                                        ) : (
                                            <p className="text-xs text-amber-300/70">Acesse o portal do associado para pagar a cobrança.</p>
                                        )}
                                    </div>
                                )}

                                {!hasAdesao && (
                                    <div className="w-full rounded-xl p-4 bg-green-500/10 border border-green-500/20">
                                        <p className="text-sm text-green-300">✅ Sua primeira mensalidade vence no dia {diaVencimento} do próximo mês.</p>
                                    </div>
                                )}

                                {/* Points earned confirmation */}
                                {!!servico.pontos_adesao && servico.pontos_adesao > 0 && (
                                    <div className="w-full rounded-xl p-4 bg-rose-500/10 border border-rose-500/20 flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                                            <Sparkles size={20} className="text-rose-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-rose-300 font-bold">+{servico.pontos_adesao} pontos Sempre Juntos</p>
                                            <p className="text-xs text-rose-300/60">Creditados automaticamente na sua conta!</p>
                                        </div>
                                    </div>
                                )}

                                {/* Manual do serviço */}
                                {servico.manual_url && (
                                    <a
                                        href={servico.manual_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-white/15 text-white/70 text-sm hover:bg-white/5 transition-all"
                                    >
                                        <ExternalLink size={15} /> Manual do Serviço (PDF)
                                    </a>
                                )}

                                {/* Redirect to portal */}
                                <div className="w-full space-y-2">
                                    <button
                                        onClick={() => navigate('/portal/contratos')}
                                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm text-white transition-all hover:brightness-110"
                                        style={{ backgroundColor: primHex }}
                                    >
                                        Ir para o Portal →
                                    </button>
                                    <p className="text-xs text-white/30 text-center">
                                        Redirecionando automaticamente em {redirectCountdown}s…
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer note */}
                    {!['loading', 'not-found', 'result'].includes(step) && (
                        <p className="text-center text-xs text-white/20 mt-6">
                            Seus dados estão seguros e criptografados.
                        </p>
                    )}
                </div>
            </main>
        </div>
    )
}
